import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { QUERIES } from '_metronic/helpers'
import { prescricoesDoHistorico } from '../core/montagem/historico'
import { getPrescricaoById, getPrescricoes } from '../core/_requests'

/** Até onde o histórico da tela vai: o suficiente para agrupar alguns meses sem paginar. */
const LIMITE_HISTORICO = 60

const chaveDetalhe = (id: number) => ['prescricao-detalhe', id]

/**
 * Prescrições assinadas do paciente feitas pelo médico logado (`GET /prescricao` só lista as do
 * próprio médico), mais recentes primeiro. Os itens vêm à parte, por prescrição.
 */
export const useHistoricoPrescricoes = (pacienteId?: number) => {
  const queryClient = useQueryClient()

  const consulta = useQuery(
    [QUERIES.PRESCRICOES_LIST, 'historico', pacienteId],
    () =>
      getPrescricoes(
        `page=0&size=${LIMITE_HISTORICO}&search=${encodeURIComponent(
          `paciente.id:${pacienteId}`
        )}&sort=data,desc`
      ),
    {
      enabled: !!pacienteId,
      refetchOnWindowFocus: false,
      select: (resposta) => prescricoesDoHistorico(resposta.data ?? []),
    }
  )

  /** Detalhe com os itens; compartilhado entre cartão, "Repetir tudo" e "Escolher itens". */
  const carregarDetalhe = useCallback(
    (id: number) =>
      queryClient.fetchQuery(chaveDetalhe(id), () => getPrescricaoById(id), {
        staleTime: 5 * 60_000,
      }),
    [queryClient]
  )

  const atualizar = useCallback(
    () => queryClient.invalidateQueries([QUERIES.PRESCRICOES_LIST, 'historico']),
    [queryClient]
  )

  return {
    prescricoes: consulta.data ?? [],
    carregando: consulta.isLoading,
    carregarDetalhe,
    atualizar,
  }
}

/** Detalhe de uma prescrição do histórico, para listar os itens no cartão. */
export const useDetalhePrescricao = (id: number, habilitado: boolean) =>
  useQuery(chaveDetalhe(id), () => getPrescricaoById(id), {
    enabled: habilitado,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
